<div class="mx-auto flex w-full max-w-[1600px] flex-col gap-3 p-3">

  <!-- HERO CARD — purely informational; column chooser + CSV export sit on the result toolbar. -->
  <div class="rounded-2xl border border-border bg-card p-4 shadow-sm">
    <div class="flex min-w-0 flex-col gap-1">
      <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
        Global tools
      </p>
      <h1 class="font-display text-2xl font-semibold leading-tight text-foreground">
        Call viewer
      </h1>
      <p class="text-sm text-muted-foreground">
        Search call history. All filters are optional — leave fields empty to broaden the search.
      </p>
    </div>
  </div>

  <!--
    FILTER BAR CARD — only rendered when expanded (default before search, or after the user
    clicks "Edit filters" on the active-filter banner). Once results are loaded the card is
    hidden entirely and the chip banner takes over as the visible filter summary.
  -->
  @if (!filterCardCollapsed()) {
  <section class="rounded-2xl border border-border bg-card shadow-sm">
    <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
      <div class="font-mono-feat text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        Filters
      </div>
      @if (callsService.rows().length) {
        <button
          hlmBtn variant="outline" size="sm"
          type="button"
          (click)="toggleFilterCard()"
        >
          <hlm-icon name="lucideChevronUp" size="0.9rem" />
          Collapse
        </button>
      }
    </header>

    <form
      [formGroup]="filterForm"
      (ngSubmit)="onSearch()"
      class="flex flex-col gap-6 p-5"
    >

      <!-- ─── SEARCH SCOPE SECTION ─── -->
      <!--
        Min-search contract: at least ONE of {Customer, User Account, Agent} + a date.
        Any combination is allowed — Customer+UA cross-validates the customerId.
      -->
      <div class="flex flex-col gap-3">
        <div class="flex flex-wrap items-baseline justify-between gap-2">
          <div class="font-mono-feat text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Search scope
          </div>
          <div class="font-mono-feat text-xs uppercase tracking-[0.10em] text-muted-foreground">
            ≥ 1 required · any combination
          </div>
        </div>
        <div class="grid gap-4 md:grid-cols-3">
          <!-- Customer ID — typeahead via Elasticsearch (search by ID, name, company) -->
          <div class="flex flex-col gap-1.5">
            <label
              for="calls-customer-id"
              class="cmt-eyebrow text-xs"
            >
              Customer
            </label>
            <cmt-id-autocomplete
              [inputId]="'calls-customer-id'"
              entityType="customer"
              idFieldFilter="customerId"
              placeholder="ID, company or name…"
              [resolvedLabel]="customerResolvedLabel()"
              formControlName="customerId"
              (hitSelected)="onCustomerHit($event)"
            />
            <span class="text-sm text-muted-foreground">Internal: RAV ID</span>
          </div>

          <!--
            User Account / Phone Service — typeahead spans both entity types because the
            two are tightly coupled in CMT and operators often know the PS-ID directly.
            Selection of either kind writes to the same form control; the parent dispatches
            to userAccountId vs phoneServiceId at search time based on selectedUserAccountHit.kind.
          -->
          <div class="flex flex-col gap-1.5">
            <label
              for="calls-ua-id"
              class="cmt-eyebrow text-xs"
            >
              User Account / Phone Service
            </label>
            <cmt-id-autocomplete
              [inputId]="'calls-ua-id'"
              [entityTypes]="['useraccount', 'phoneservice']"
              [idFieldFilters]="['userAccountId', 'phoneServiceId']"
              placeholder="UA-ID, PS-ID, name or description…"
              [resolvedLabel]="userAccountResolvedLabel()"
              formControlName="userAccountId"
              (hitSelected)="onUserAccountHit($event)"
            />
            <span class="text-sm text-muted-foreground">Selecting either auto-fills the parent Customer</span>
          </div>

          <!-- Agent / Employee — typeahead via /phoneservice/v3/employees?search=… (3+ chars). -->
          <div class="flex flex-col gap-1.5">
            <label
              for="calls-agent-id"
              class="cmt-eyebrow text-xs"
            >
              Agent/Employee
            </label>
            <cmt-id-autocomplete
              [inputId]="'calls-agent-id'"
              [customSearchFn]="employeeSearchFn"
              [minChars]="3"
              placeholder="ID, login or name (≥ 3 chars)…"
              [resolvedLabel]="agentResolvedLabel()"
              formControlName="agentId"
              (hitSelected)="onAgentHit($event)"
            />
            <span class="text-sm text-muted-foreground">Type at least 3 characters</span>
          </div>

        </div>
      </div>

      <!-- ─── TIME RANGE SECTION ─── -->
      <div class="flex flex-col gap-3">
        <!-- Section eyebrow on its own line so the preset row below has clean baseline alignment. -->
        <div class="font-mono-feat text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Time range
        </div>

        <!-- Preset chips — full row, separated from labels so they don't visually clash. -->
        <div class="flex flex-wrap items-center gap-1.5">
          @for (preset of timePresets; track preset.key) {
            <button
              hlmBtn
              [variant]="activePreset() === preset.key ? 'default' : 'outline'"
              size="sm"
              type="button"
              (click)="applyTimePreset(preset.key)"
            >
              {{ preset.label }}
            </button>
          }
        </div>

        @if (dateMode() === 'single') {
          <!-- Single-day mode: one `<input type="date">`. Default is today. -->
          <div class="grid gap-4 md:grid-cols-2">
            <div class="flex flex-col gap-1.5">
              <label
                for="calls-single-date"
                class="cmt-eyebrow text-xs"
              >
                <hlm-icon name="lucideCalendar" size="0.8rem" class="mr-1 inline align-[-0.15em]" />
                On date · whole day (00:00 – 23:59)
              </label>
              <input
                id="calls-single-date"
                hlmInput
                lang="en-GB"
                type="date"
                formControlName="singleDate"
                class="w-full"
              />
            </div>
          </div>
        } @else {
          <!-- Range mode: from + to. -->
          <div class="grid gap-4 md:grid-cols-2">
            <div class="flex flex-col gap-1.5">
              <label
                for="calls-start"
                class="cmt-eyebrow text-xs"
              >
                <hlm-icon name="lucideCalendar" size="0.8rem" class="mr-1 inline align-[-0.15em]" />
                From
              </label>
              <input
                id="calls-start"
                hlmInput
                lang="en-GB"
                type="datetime-local"
                formControlName="start"
                class="w-full"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label
                for="calls-end"
                class="cmt-eyebrow text-xs"
              >
                <hlm-icon name="lucideClock" size="0.8rem" class="mr-1 inline align-[-0.15em]" />
                To
              </label>
              <input
                id="calls-end"
                hlmInput
                lang="en-GB"
                type="datetime-local"
                formControlName="end"
                class="w-full"
              />
            </div>
          </div>
        }
      </div>

      <!-- Action bar: Reset + Search -->
      <div class="flex flex-wrap items-center justify-end gap-3 border-t border-border pt-4">
        @if (!hasIdentityScope() || !hasDateScope()) {
          <span class="font-mono-feat text-xs text-muted-foreground">
            Search needs identity (Customer / UA / Agent) + a date
          </span>
        }
        <button
          hlmBtn variant="outline" size="default"
          type="button"
          (click)="onReset()"
          [disabled]="callsService.loading()"
        >
          <hlm-icon name="lucideRotateCcw" size="0.9rem" />
          Reset
        </button>
        <button
          hlmBtn variant="default" size="default"
          type="submit"
          class="min-w-[140px]"
          [disabled]="!canSearch() || callsService.loading()"
        >
          @if (callsService.loading()) {
            <hlm-icon name="lucideRefreshCw" size="0.9rem" class="animate-spin" />
            Searching…
          } @else {
            <hlm-icon name="lucideSearch" size="0.9rem" />
            Search
          }
        </button>
      </div>

      <!-- Inline validation / warnings -->
      @if (hasEndBeforeStartError()) {
        <hlm-alert tone="error">
          <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5" />
          <span>"To" must be after "From".</span>
        </hlm-alert>
      }
      @if (hasUnresolvedAutocomplete()) {
        <hlm-alert tone="error">
          <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5" />
          <span>Pick a Customer or User Account from the dropdown — typed text alone isn't a valid filter.</span>
        </hlm-alert>
      }
      @if (hasCustomerUaMismatch()) {
        <hlm-alert tone="error">
          <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5" />
          <span>The selected User Account doesn't belong to the entered Customer — clear one of them.</span>
        </hlm-alert>
      }
      @if (!hasIdentityScope()) {
        <hlm-alert tone="warn">
          <hlm-icon name="lucideInfo" size="1rem" class="mt-0.5" />
          <span>Add a Customer, User Account, or Agent — Search needs at least one of them.</span>
        </hlm-alert>
      }
      @if (!hasDateScope()) {
        <hlm-alert tone="warn">
          <hlm-icon name="lucideInfo" size="1rem" class="mt-0.5" />
          <span>Pick a date or time range — Search needs a time scope.</span>
        </hlm-alert>
      }
      @if (hasUnscopedAgentWarn()) {
        <hlm-alert tone="warn">
          <hlm-icon name="lucideInfo" size="1rem" class="mt-0.5" />
          <span>Agent search benefits from a Customer ID, User Account ID, or time range — otherwise the result set may be very large.</span>
        </hlm-alert>
      }
      @if (rangeWarn(); as warn) {
        <hlm-alert tone="warn">
          <hlm-icon name="lucideInfo" size="1rem" class="mt-0.5" />
          <span>{{ warn }}</span>
        </hlm-alert>
      }
    </form>
  </section>
  }

  <!-- Error banner -->
  @if (callsService.error(); as err) {
    <hlm-alert tone="error">
      <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5" />
      <div class="flex flex-1 flex-col gap-2">
        <div>
          Failed to load calls (HTTP {{ err.status }}).
          @if (err.correlationId) {
            <span class="font-mono-feat text-xs text-muted-foreground">correlation-id: {{ err.correlationId }}</span>
          }
        </div>
        <div>
          <button hlmBtn variant="outline" size="sm" type="button" (click)="onRetry()">
            <hlm-icon name="lucideRefreshCw" size="0.9rem" />
            Retry
          </button>
        </div>
      </div>
    </hlm-alert>
  }

  <!-- GRID CARD -->
  <section class="rounded-2xl border border-border bg-card p-4 shadow-sm">
    <!--
      Active-filter banner — visual summary + Edit filters action (when card is collapsed).
      Replaces the separate filter-card header once results are loaded.
    -->
    @if (activeFilters(); as af) {
      @if (af.customerId || af.userAccountId || af.phoneServiceId || af.agentId || af.start || af.end) {
        <div class="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
          <span class="font-mono-feat text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            Active filters
          </span>
          @if (af.customerId) {
            <span class="cmt-filter-chip">
              <span class="cmt-filter-chip-body">
                <span class="text-muted-foreground">Customer:</span>&nbsp;<span class="text-foreground">{{ af.customerLabel || '—' }}</span>
                <span class="font-mono-feat text-muted-foreground">({{ af.customerId }})</span>
              </span>
              <button type="button" class="cmt-chip-clear" (click)="removeActiveFilter('customerId')" aria-label="Remove customer filter" title="Remove customer filter">
                <hlm-icon name="lucideX" size="0.7rem" />
              </button>
            </span>
          }
          @if (af.userAccountId) {
            <span class="cmt-filter-chip">
              <span class="cmt-filter-chip-body">
                <span class="text-muted-foreground">User Account:</span>&nbsp;<span class="text-foreground">{{ af.userAccountLabel || '—' }}</span>
                <span class="font-mono-feat text-muted-foreground">({{ af.userAccountId }})</span>
              </span>
              <button type="button" class="cmt-chip-clear" (click)="removeActiveFilter('userAccountId')" aria-label="Remove user account filter" title="Remove user account filter">
                <hlm-icon name="lucideX" size="0.7rem" />
              </button>
            </span>
          }
          @if (af.phoneServiceId) {
            <span class="cmt-filter-chip">
              <span class="cmt-filter-chip-body">
                <span class="text-muted-foreground">Phone Service:</span>&nbsp;<span class="text-foreground">{{ af.phoneServiceLabel || '—' }}</span>
                <span class="font-mono-feat text-muted-foreground">({{ af.phoneServiceId }})</span>
              </span>
              <button type="button" class="cmt-chip-clear" (click)="removeActiveFilter('userAccountId')" aria-label="Remove phone service filter" title="Remove phone service filter">
                <hlm-icon name="lucideX" size="0.7rem" />
              </button>
            </span>
          }
          @if (af.agentId) {
            <span class="cmt-filter-chip">
              <span class="cmt-filter-chip-body">
                <span class="text-muted-foreground">Agent:</span>&nbsp;<span class="text-foreground">{{ af.agentLabel || '—' }}</span>
                <span class="font-mono-feat text-muted-foreground">({{ af.agentId }})</span>
              </span>
              <button type="button" class="cmt-chip-clear" (click)="removeActiveFilter('agentId')" aria-label="Remove agent filter" title="Remove agent filter">
                <hlm-icon name="lucideX" size="0.7rem" />
              </button>
            </span>
          }
          @if (af.start || af.end) {
            <span class="cmt-filter-chip">
              <span class="cmt-filter-chip-body">
                Range&nbsp;<span class="font-mono-feat text-foreground">{{ af.rangePresetLabel ?? 'Custom' }}</span>
              </span>
              <!-- A date is mandatory, so this RESETS to Today (it can't be removed like the
                   identity chips). Distinct ↺ icon + tooltip signal reset, not remove. -->
              <button type="button" class="cmt-chip-clear" (click)="removeActiveFilter('rangePresetLabel')" aria-label="Reset time range to today" title="Reset time range to today">
                <hlm-icon name="lucideRotateCcw" size="0.7rem" />
              </button>
            </span>
          }
          @if (filterCardCollapsed()) {
            <!--
              Once results are loaded the filter card collapses, so this button
              is the only way back to the form. Promoted from outline+sm to a
              filled primary button so it visually dominates the active-filter
              chip row and reads as the chip-row's main action.
            -->
            <button
              hlmBtn variant="default"
              type="button"
              class="ml-auto"
              (click)="toggleFilterCard()"
            >
              <hlm-icon name="lucideSlidersHorizontal" size="0.9rem" />
              Edit filters
            </button>
          }
        </div>
      }
    }

    <!-- Sub-toolbar: client-side quick filter + result counter -->
    <div class="mb-2 flex flex-wrap items-center gap-3">
      <div class="relative min-w-[260px] flex-1">
        <hlm-icon
          name="lucideSearch"
          size="0.9rem"
          class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          hlmInput
          type="text"
          class="w-full pl-9"
          placeholder="Filter visible rows by text (client-side — applies to loaded calls only)"
          [value]="quickFilter()"
          (input)="onQuickFilterInput($event)"
          aria-label="Filter visible rows"
        />
      </div>
      <div class="font-mono-feat text-xs text-muted-foreground">
        @if (callsService.rows().length) {
          @if (quickFilter()) {
            <span class="font-semibold text-foreground">{{ visibleRowCount() }}</span>
            of <span class="font-semibold text-foreground">{{ callsService.rows().length }}</span> visible
          } @else {
            <span class="font-semibold text-foreground">{{ callsService.rows().length }}</span>
            {{ callsService.rows().length === 1 ? 'call' : 'calls' }} loaded
          }
          @if (loadedAtLabel(); as loadedAt) {
            <span class="text-muted-foreground/70" [title]="loadedAtFull()">· loaded {{ loadedAt }}</span>
          }
        } @else {
          No calls loaded
        }
      </div>

      <!-- Result actions — Reload (freshness), Reset (only when columns differ from default), Columns chooser, CSV. -->
      <div class="flex flex-none items-center gap-2">
        @if (callsService.loadedAt() && filterCardCollapsed()) {
          <button
            hlmBtn variant="outline" size="sm"
            type="button"
            (click)="reloadCalls()"
            [disabled]="callsService.loading()"
            hlmTooltip="Reload — fetch fresh results for the current filters"
          >
            <hlm-icon name="lucideRefreshCw" size="0.9rem" />
            Reload
          </button>
        }
        @if (visibleFields().size > 0) {
          <button
            hlmBtn variant="outline" size="sm"
            type="button"
            (click)="resetColumns()"
            hlmTooltip="Restore default visible columns"
          >
            <hlm-icon name="lucideRotateCcw" size="0.9rem" />
            Reset cols
          </button>
        }
        <hlm-menu align="end" width="min-w-[16rem]">
          <button
            hlmBtn variant="outline" size="sm"
            hlmMenuTrigger
            hlmTooltip="Toggle columns"
            type="button"
          >
            <hlm-icon name="lucideSettings" size="0.9rem" />
            Columns
          </button>
          <ng-template hlmMenuContent>
            <!-- text-left is required because hlm-menu sets text-align:end on the popover,
                 which would right-align the inline-flex hlm-checkbox rows. -->
            <div class="cmt-eyebrow px-3 pb-1 pt-2 text-left text-xs">
              Optional columns
            </div>
            <div class="flex flex-col gap-1 p-1 text-left">
              @for (col of toggleableColumns; track col.field) {
                <hlm-checkbox
                  class="flex w-full items-center justify-start rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted"
                  [label]="col.label"
                  [checked]="isColumnVisible(col.field)"
                  (checkedChange)="toggleColumn(col.field, $event)"
                />
              }
            </div>
          </ng-template>
        </hlm-menu>
        <button
          hlmBtn variant="outline" size="sm"
          type="button"
          (click)="onExportCsv()"
          [disabled]="!callsService.rows().length"
          hlmTooltip="Download all loaded rows as CSV"
        >
          <hlm-icon name="lucideDownload" size="0.9rem" />
          Download CSV
        </button>
        @if (activeFilters()) {
          <hlm-menu align="end" width="min-w-[14rem]">
            <button
              hlmBtn variant="outline" size="sm"
              hlmMenuTrigger
              type="button"
              hlmTooltip="Share this exact search"
            >
              <hlm-icon name="lucideLink" size="0.9rem" />
              Share
            </button>
            <ng-template hlmMenuContent>
              <div class="px-3 pb-1.5 pt-2.5 font-mono-feat text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Share search
              </div>
              <div class="flex flex-col gap-0.5 p-1.5">
                <button
                  hlmMenuItem
                  class="!gap-3 !px-3 !py-2.5 !text-sm rounded-md transition-colors hover:!bg-primary/10 hover:!text-foreground focus:!bg-primary/15"
                  (click)="onShareCopy()"
                >
                  <hlm-icon name="lucideLink" size="1rem" class="text-muted-foreground" />
                  <span>Copy link</span>
                </button>
                <button
                  hlmMenuItem
                  class="!gap-3 !px-3 !py-2.5 !text-sm rounded-md transition-colors hover:!bg-primary/10 hover:!text-foreground focus:!bg-primary/15"
                  (click)="onShareEmail()"
                >
                  <hlm-icon name="lucideMail" size="1rem" class="text-muted-foreground" />
                  <span>Email…</span>
                </button>
                <button
                  hlmMenuItem
                  class="!gap-3 !px-3 !py-2.5 !text-sm rounded-md transition-colors hover:!bg-primary/10 hover:!text-foreground focus:!bg-primary/15"
                  (click)="onShareTeams()"
                >
                  <hlm-icon name="lucideMessageSquare" size="1rem" class="text-muted-foreground" />
                  <span>Send via Teams…</span>
                </button>
              </div>
            </ng-template>
          </hlm-menu>
        }
      </div>
    </div>

    <!--
      Status legend — moved inline below the toolbar (same row would crowd the search box).
      Each swatch is a tiny row sample (.cmt-legend-row) sharing the EXACT same color-mix
      recipe as the AG-Grid row tint, so the legend literally shows the row background —
      not the status badge color. Single tight row to save vertical real estate.
    -->
    @if (callsService.rows().length) {
      <!-- Tint chips double as quick filters: clicking a chip toggles a row-status filter
           via AG-Grid's external filter API. Multi-select works (click several).
           Discoverability cues: lucideFilter icon prefix + 'Quick filter' label + 'click to
           filter' hint + cmt-pulse-hint animation on the chips for the first ~2.4s of
           every load — draws the eye without being noisy. Labelled "Quick filter" to
           distinguish from the text-input filter above which already says "Filter visible
           rows by text…" — two "Filter" words on the same row felt ambiguous. -->
      <div class="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span class="inline-flex items-center gap-1.5 font-mono-feat font-semibold uppercase tracking-[0.10em]">
          <hlm-icon name="lucideFilter" size="0.8rem" class="text-primary" />
          Quick filter
        </span>
        <button type="button" class="cmt-legend-row cmt-row-busy" [class.cmt-tint-active]="tintFilter().has('BUSY')" [class.cmt-pulse-hint]="tintFilter().size === 0" (click)="toggleTintFilter('BUSY')" [attr.aria-pressed]="tintFilter().has('BUSY')" hlmTooltip="Click to show only BUSY calls">BUSY</button>
        <span>busy</span>
        <button type="button" class="cmt-legend-row cmt-row-ring" [class.cmt-tint-active]="tintFilter().has('RING')" [class.cmt-pulse-hint]="tintFilter().size === 0" (click)="toggleTintFilter('RING')" [attr.aria-pressed]="tintFilter().has('RING')" hlmTooltip="Click to show only RING calls">RING</button>
        <span>no answer</span>
        <button type="button" class="cmt-legend-row cmt-row-notiz" [class.cmt-tint-active]="tintFilter().has('NOTIZ')" [class.cmt-pulse-hint]="tintFilter().size === 0" (click)="toggleTintFilter('NOTIZ')" [attr.aria-pressed]="tintFilter().has('NOTIZ')" hlmTooltip="Click to show only calls with a callnote">Notiz</button>
        <span>callnote</span>
        <button type="button" class="cmt-legend-row" [class.cmt-tint-active]="tintFilter().has('OTHER')" [class.cmt-pulse-hint]="tintFilter().size === 0" (click)="toggleTintFilter('OTHER')" [attr.aria-pressed]="tintFilter().has('OTHER')" hlmTooltip="Click to show calls without a colored tint">other</button>
        @if (tintFilter().size > 0) {
          <button type="button" class="cmt-chip-clear ml-1" (click)="clearTintFilter()" hlmTooltip="Clear tint filter" aria-label="Clear tint filter">
            <hlm-icon name="lucideX" size="0.7rem" />
          </button>
          <span class="font-mono-feat text-muted-foreground">·</span>
          <span class="font-mono-feat font-semibold text-foreground">{{ visibleRowCount() }}</span>
          <span>of</span>
          <span class="font-mono-feat">{{ callsService.rows().length }}</span>
          <span>shown</span>
        } @else {
          <span class="italic text-muted-foreground/70">— click a chip to filter</span>
        }
      </div>
    }

    <!--
      Grid height: viewport-based calc — calc(100vh-19rem) tuned for the tightened chrome
      (gap-3, p-4 cards, mb-2 toolbar, single-row legend). Page should fit without a global
      vertical scroll so the grid's horizontal scrollbar stays inside the viewport.
    -->
    <!-- cmt-grid-wrap: relative so the right-edge fade can sit absolutely positioned;
         the fade only shows when columns overflow horizontally (driven by .cmt-has-overflow). -->
    <div class="cmt-grid-wrap relative h-[calc(100vh-27rem)] min-h-[300px] overflow-hidden rounded-xl border border-border" [class.cmt-has-overflow]="hasHorizontalOverflow()">
      <ag-grid-angular
        class="ag-theme-material cmt-grid"
        [rowData]="callsService.rows()"
        [columnDefs]="columnDefs"
        [defaultColDef]="defaultColDef"
        [getRowClass]="getRowClass"
        [isExternalFilterPresent]="isExternalFilterPresent"
        [doesExternalFilterPass]="doesExternalFilterPass"
        [pagination]="true"
        [paginationAutoPageSize]="true"
        [quickFilterText]="quickFilter()"
        [overlayNoRowsTemplate]="noRowsOverlayTemplate"
        [suppressHorizontalScroll]="false"
        [tooltipShowDelay]="400"
        (gridReady)="onGridReady($event)"
        (firstDataRendered)="onFirstDataRendered($event)"
        (gridSizeChanged)="onGridSizeChanged($event)"
        (rowClicked)="onRowClicked($event)"
      />
      <!--
        Loading overlay — driven by callsService.loading() signal so it
        appears synchronously the moment a search fires. Independent of
        AG-Grid's internal overlay state, which races with rowData=[] and
        the noRowsOverlay (and needs gridApi to be ready, which it isn't
        on the very first click).
      -->
      @if (callsService.loading()) {
        <div class="pointer-events-none absolute inset-0 z-10 flex items-start justify-center bg-card/85 backdrop-blur-[1px]">
          <div class="mt-10 flex w-full max-w-xl flex-col items-center gap-4 px-8 text-muted-foreground">
            <div class="flex w-full flex-col items-center gap-2">
              <div class="text-lg font-semibold text-foreground">Loading calls…</div>
              <div class="text-sm">Large date ranges may take a few seconds.</div>
            </div>
            <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div class="h-full w-1/3 animate-indeterminate rounded-full bg-primary"></div>
            </div>
          </div>
        </div>
      }
    </div>
  </section>

</div>
